import { motion } from "framer-motion";
import {
  ArrowRight,
  CalendarCheck2,
  CheckCircle2,
  Compass,
  FileCheck2,
  GraduationCap
} from "lucide-react";
import { Link } from "react-router-dom";
import { heroContent } from "../../data/homeData";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.68,
      ease: [0.16, 1, 0.3, 1]
    }
  })
};

const roadmapIcons = [Compass, GraduationCap, FileCheck2];

function HeroSection() {
  const scrollToDestinations = (event) => {
    event.preventDefault();
    const destinationSection = document.getElementById("destinations");

    if (destinationSection) {
      destinationSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="home" className="relative overflow-hidden pb-16 pt-28 md:pb-24 md:pt-36">
      <div className="absolute top-0 right-0 -z-10 w-full h-[120%] bg-gradient-to-b from-royal-blue/5 to-transparent pointer-events-none" />
      <div className="absolute top-24 right-0 -z-10 w-[600px] h-[600px] bg-royal-blue/10 blur-[100px] rounded-full pointer-events-none" />

      <div className="relative mx-auto grid w-full max-w-7xl gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <motion.p
            custom={0.04}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center rounded-full bg-royal-blue/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-royal-blue md:text-xs"
          >
            {heroContent.eyebrow}
          </motion.p>

          <motion.h1
            custom={0.12}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-6 max-w-2xl text-3xl leading-[1.12] tracking-tight text-midnight-navy sm:text-4xl md:text-6xl"
          >
            {heroContent.title}
          </motion.h1>

          <motion.p
            custom={0.2}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-text/80 sm:text-base md:text-lg"
          >
            {heroContent.subtitle}
          </motion.p>

          <motion.ul
            custom={0.26}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-8 grid gap-3 sm:grid-cols-2"
          >
            {heroContent.supportPoints.map((point) => (
              <li key={point} className="flex items-start gap-2.5 text-sm font-medium text-slate-text/90">
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-royal-blue shrink-0" aria-hidden="true" />
                <span>{point}</span>
              </li>
            ))}
          </motion.ul>

          <motion.div
            custom={0.32}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <Link
              to={heroContent.primaryCta.to}
              data-flight-message="Come on, book your free counselling now."
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-midnight-navy px-6 py-3.5 text-sm font-semibold text-soft-white shadow-[0_8px_20px_-8px_rgba(7,18,38,0.5)] transition hover:bg-royal-blue hover:shadow-[0_12px_24px_-8px_rgba(15,111,255,0.4)]"
            >
              {heroContent.primaryCta.label}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href={heroContent.secondaryCta.to}
              onClick={scrollToDestinations}
              data-flight-message="Come on, know about these destinations."
              className="inline-flex items-center justify-center rounded-xl border border-midnight-navy/15 bg-white px-6 py-3.5 text-sm font-semibold text-midnight-navy transition hover:border-royal-blue/30 hover:bg-royal-blue/5"
            >
              {heroContent.secondaryCta.label}
            </a>
          </motion.div>

          <motion.div
            custom={0.38}
            variants={reveal}
            initial="hidden"
            animate="visible"
            className="mt-10 flex flex-wrap gap-x-8 gap-y-4 pt-8 border-t border-midnight-navy/10"
          >
            {heroContent.quickTrustPoints.map((point) => (
              <div key={point.label} className="flex flex-col">
                <p className="text-lg font-bold text-midnight-navy sm:text-xl">{point.value}</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-text/60">{point.label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative lg:ml-auto w-full max-w-lg"
        >
          <div className="card-elevated overflow-hidden p-2">
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={heroContent.heroImage}
                alt="Students discussing international education plans"
                className="h-64 w-full object-cover md:h-80"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-midnight-navy/90 via-midnight-navy/10 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-xl bg-white/20 px-4 py-3 text-soft-white backdrop-blur-md border border-white/10">
                <p className="text-xs font-semibold uppercase tracking-[0.12em]">Next Available Slots</p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-warm-gold/20 px-2 py-1 rounded">
                  <CalendarCheck2 className="h-3.5 w-3.5 text-warm-gold" aria-hidden="true" />
                  This Week
                </span>
              </div>
            </div>

            <div className="mt-2 flex flex-col gap-2 p-2">
              {heroContent.floatingCards.map((step, index) => {
                const Icon = roadmapIcons[index];
                return (
                  <div
                    key={step}
                    className="flex items-center gap-3 rounded-xl bg-soft-white/60 px-4 py-3 transition hover:bg-royal-blue/5"
                  >
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-midnight-navy/5 text-royal-blue">
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    <p className="text-sm font-semibold text-midnight-navy">{step}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;
